/**
 * ABK Cart Guard
 * 1. Shows a warning and blocks checkout when the cart contains
 *    both ABK-CARD and ABK-CARD-MINI products (type conflict).
 * 2. Shows a warning and blocks checkout when the cart contains
 *    ABK-CARD products mixed with non-ABK-CARD products (mixed cart).
 * 3. Shows a warning and blocks checkout when a Capodanno package
 *    (SKU prefix CAPODANNO27-) has fewer than 2 people in the cart.
 *    SKU: CAPODANNO27-<package>-<cenone>-<bus>. People are summed per
 *    package (everything before cenone and bus), so the same package
 *    split across cenone/bus options counts together.
 * Loaded on the cart page via main-cart.liquid.
 */

const ABK_PREFIXES = ['ABK-CARD', 'ABK-MINI-CARD'];

function isCardSku(sku) {
  if (typeof sku !== 'string') return false;
  const upper = sku.toUpperCase();
  return ABK_PREFIXES.some((prefix) => upper.startsWith(prefix));
}

function getCardTypes(items) {
  const types = new Set();
  for (const item of items) {
    if (isCardSku(item.sku)) {
      types.add(item.sku.toUpperCase());
    }
  }
  return types;
}

function hasTypeConflict(items) {
  return getCardTypes(items).size > 1;
}

function hasMixedCart(items) {
  let hasCard = false;
  let hasOther = false;
  for (const item of items) {
    if (isCardSku(item.sku)) {
      hasCard = true;
    } else {
      hasOther = true;
    }
    if (hasCard && hasOther) return true;
  }
  return false;
}

const CAPODANNO_PREFIX = 'CAPODANNO27-';
const CAPODANNO_MIN_PEOPLE = 2;

function capodannoPackageKey(sku) {
  return sku.toUpperCase().split('-').slice(0, -2).join('-');
}

function hasCapodannoUnderMin(items) {
  const peopleByPackage = new Map();
  for (const item of items) {
    if (typeof item.sku !== 'string' || !item.sku.toUpperCase().startsWith(CAPODANNO_PREFIX)) continue;
    const key = capodannoPackageKey(item.sku);
    peopleByPackage.set(key, (peopleByPackage.get(key) || 0) + item.quantity);
  }
  for (const people of peopleByPackage.values()) {
    if (people < CAPODANNO_MIN_PEOPLE) return true;
  }
  return false;
}

function setBlocked(checkout, attr, blocked) {
  if (!checkout) return;
  if (blocked) {
    checkout.setAttribute(attr, '');
  } else {
    checkout.removeAttribute(attr);
  }
}

function updateCheckoutState(checkout) {
  if (!checkout) return;
  const isBlocked =
    checkout.hasAttribute('data-abk-conflict-blocked') ||
    checkout.hasAttribute('data-abk-mixed-blocked') ||
    checkout.hasAttribute('data-abk-capodanno-min-blocked') ||
    checkout.hasAttribute('data-traveler-blocked');
  checkout.disabled = isBlocked;
}

async function checkCart() {
  const conflictBanner = document.querySelector('[data-abk-conflict-banner]');
  const mixedBanner = document.querySelector('[data-abk-mixed-banner]');
  const capodannoMinBanner = document.querySelector('[data-abk-capodanno-min-banner]');
  const checkout = document.querySelector('[name="checkout"]');
  if (!conflictBanner && !mixedBanner && !capodannoMinBanner) return;

  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();

    const typeConflict = hasTypeConflict(cart.items);
    const mixed = hasMixedCart(cart.items);
    const capodannoUnderMin = hasCapodannoUnderMin(cart.items);

    if (conflictBanner) conflictBanner.hidden = !typeConflict;
    if (mixedBanner) mixedBanner.hidden = !mixed;
    if (capodannoMinBanner) capodannoMinBanner.hidden = !capodannoUnderMin;

    setBlocked(checkout, 'data-abk-conflict-blocked', typeConflict);
    setBlocked(checkout, 'data-abk-mixed-blocked', mixed);
    setBlocked(checkout, 'data-abk-capodanno-min-blocked', capodannoUnderMin);
    updateCheckoutState(checkout);
  } catch { /* ignore */ }
}

// Check on load
checkCart();

// Re-check after cart updates (item removed, quantity changed)
document.addEventListener('cart:updated', () => checkCart());
